import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Overline, SectionCard, Tag, ScoreBar, EmptyState, Disclaimer } from "../components/shared";
import { cn } from "../lib/utils";
import { Target, Loader2, TrendingUp, Users, ShieldAlert, Coins } from "lucide-react";

const TREND_TONE = {
  Emerging: "text-blue-700 bg-blue-50 border-blue-200",
  Growing: "text-emerald-700 bg-emerald-50 border-emerald-200",
  Overcrowded: "text-red-700 bg-red-50 border-red-200",
  Declining: "text-amber-700 bg-amber-50 border-amber-200",
};

export default function MarketGaps() {
  const [opps, setOpps] = useState(null);

  useEffect(() => { api.get("/opportunities").then((r) => setOpps(r.data.opportunities)); }, []);

  if (!opps) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-5 fade-up" data-testid="market-gaps-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><Overline>Opportunity Intelligence</Overline><h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Market Gaps & Opportunities</h1></div>
        <div className="text-xs text-muted-foreground">Derived from the full tracked market</div>
      </div>
      <Disclaimer>AI-identified opportunities from database patterns. Correlation is not causation — validate independently before acting.</Disclaimer>

      {opps.length === 0 ? <EmptyState title="No opportunities identified yet" icon={Target} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {opps.map((o) => (
            <div key={o.id} className="bg-card border border-border/60 p-5 hover:shadow-md transition-shadow" data-testid={`opportunity-${o.id}`}>
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-heading font-bold text-lg tracking-tight leading-snug">{o.name}</h3>
                <span className={cn("shrink-0 inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-semibold", TREND_TONE[o.trend] || TREND_TONE.Emerging)}>{o.trend}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{o.problem}</p>

              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div className="flex items-start gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" /><span><span className="font-semibold">Market evidence:</span> {o.market_evidence}</span></div>
                <div className="flex items-start gap-1.5"><Users className="w-3.5 h-3.5 text-indigo-600 mt-0.5 shrink-0" /><span><span className="font-semibold">Competitors:</span> {o.competitor_count}</span></div>
                <div className="flex items-start gap-1.5"><ShieldAlert className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" /><span><span className="font-semibold">Weaknesses:</span> {o.current_weaknesses}</span></div>
                <div className="flex items-start gap-1.5"><Coins className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /><span><span className="font-semibold">Monetization:</span> {o.monetization}</span></div>
              </div>

              <div className="mt-3 border-t border-border/50 pt-3">
                <div className="overline text-muted-foreground mb-1">Proposed Differentiation</div>
                <p className="text-sm">{o.proposed_differentiation}</p>
              </div>

              <div className="mt-3 flex flex-wrap gap-1.5">
                <Tag>Blockchain value: {o.blockchain_value}</Tag>
                <Tag>Token necessity: {o.token_necessity}</Tag>
                <Tag>Complexity: {o.complexity}</Tag>
                <Tag>Reg. difficulty: {o.regulatory_difficulty}</Tag>
              </div>

              {(o.relevant_projects || []).length > 0 && (
                <div className="mt-3"><div className="overline text-muted-foreground mb-1">Relevant Projects</div><div className="flex flex-wrap gap-1.5">{o.relevant_projects.map((n) => <Tag key={n}>{n}</Tag>)}</div></div>
              )}

              <div className="mt-4">
                <div className="flex items-center justify-between text-xs mb-1"><span className="overline text-muted-foreground">Opportunity confidence</span><span className="num font-bold">{o.confidence}%</span></div>
                <ScoreBar score={o.confidence} showValue={false} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
